import { defineStore } from "pinia"
import { doLogout, loginState, loginWithGoogle, loginWithPassword } from "./api"

export interface User {
  email: string
  username: string
  superuser: boolean
}

interface BaseState {
  googleClientId: string | null
  authError: boolean
}

interface AuthenticatedState<T extends User> extends BaseState {
  authenticated: true
  user: T
}

interface UnauthenticatedState extends BaseState {
  authenticated: false
  user: null
}

export function useAuthStore<T extends User = User>() {
  return defineStore("auth", {
    state: () =>
      ({
        authenticated: false,
        user: null,
        googleClientId: null,
        authError: false,
      }) as AuthenticatedState<T> | UnauthenticatedState,
    actions: {
      async refreshUserData() {
        const response = await loginState<T>()
        if (response.authenticated) {
          this.loginSuccess(response.user)
        } else {
          this.authenticated = false
          this.user = null
        }
      },
      async passwordLogin(form: { username: string; password: string }) {
        const response = await loginWithPassword<T>(form)
        if (response.authenticated) {
          this.loginSuccess(response.user)
        } else {
          this.loginFailed()
        }
      },
      async googleLogin(id_token: string) {
        const response = await loginWithGoogle<T>({ id_token })
        if (response.authenticated) {
          this.loginSuccess(response.user)
        }
      },
      async logout() {
        await doLogout()
        this.setLogout()
      },
      setLogout() {
        this.authenticated = false
        this.user = null
      },
      addGoogleClientId(id: string) {
        this.googleClientId = id
      },
      loginSuccess(user: T) {
        this.authenticated = true
        // @ts-ignore unwrap ref not unwrapping or something
        this.user = user
        this.authError = false
        const next = new URLSearchParams(window.location.search).get("next")
        if (next !== null) {
          window.location.replace(next)
        }
      },
      loginFailed() {
        this.authenticated = false
        this.user = null
        this.authError = true
      },
      newEmail(email: string) {
        if (this.user) {
          this.user.email = email
        }
      },
      newUsername(username: string) {
        if (this.user) {
          this.user.username = username
        }
      },
    },
  })()
}
